import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bookingApi } from '../../utils/bookingApi';
import { colourHex } from '../../utils/sessionTypes';
import { formatDate } from '../../utils/dates';
import { InlineError } from '../ui';
import './ApprovalsWaiting.css';

// For coaches: gymnasts who can't book anything until they're approved
// (every type in use needs approval), with a button per type to approve them.
// Shows nothing when no one is waiting.
export default function ApprovalsWaiting() {
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    bookingApi.getApprovalsWaiting().then(res => setData(res.data)).catch(() => setLoadFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loadFailed) return <InlineError onRetry={load}>Couldn't load who's waiting for approval.</InlineError>;
  if (!data?.gymnasts?.length) return null;

  const approve = async (g, t) => {
    setBusy(g.id);
    setError(null);
    try {
      await bookingApi.setSessionTypeApproval(g.id, t.id, true);
      setData(d => ({ ...d, gymnasts: d.gymnasts.filter(x => x.id !== g.id) }));
    } catch (err) {
      setError({ id: g.id, message: err.response?.data?.error || "Couldn't approve them." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="approvals-waiting" aria-labelledby="approvals-waiting-title">
      <h2 id="approvals-waiting-title">Waiting for approval ({data.gymnasts.length})</h2>
      <p className="bk-muted">These gymnasts can't book any sessions until a coach approves them.</p>
      <ul>
        {data.gymnasts.map(g => {
          const family = g.family.map(u => `${u.firstName} ${u.lastName}`).join(', ');
          return (
            <li key={g.id}>
              <div className="approvals-waiting__who">
                <span className="approvals-waiting__name">{g.firstName} {g.lastName}</span>
                <span className="bk-muted">
                  Joined {formatDate(g.joinedAt)}
                  {g.family[0] && <>{' · '}<Link to={`/booking/admin/members/${g.family[0].id}`}>{family}</Link></>}
                </span>
              </div>
              <div className="bk-row-actions">
                {data.types.map(t => (
                  <button key={t.id} type="button" className="bk-btn bk-btn--sm" disabled={busy === g.id} onClick={() => approve(g, t)}>
                    <span className="approvals-waiting__dot" style={{ background: colourHex(t.colour) }} aria-hidden="true" />
                    Approve for {t.name}
                  </button>
                ))}
              </div>
              {error?.id === g.id && <p className="bk-error" role="alert">{error.message}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
